import React, { useState, useEffect } from 'react';
import { useUnifiedAuth } from './UnifiedAuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  Clock, ChevronLeft, ChevronRight, Calendar, 
  Download, Share2, Loader2, AlertTriangle, 
  LogIn, LogOut, Coffee, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPOS_MARCACAO = {
  'entrada': { label: 'Entrada', icon: LogIn, color: 'text-emerald-400' },
  'saida': { label: 'Saída', icon: LogOut, color: 'text-red-400' },
  'intervalo_início': { label: 'Início Intervalo', icon: Coffee, color: 'text-amber-400' },
  'intervalo_fim': { label: 'Fim Intervalo', icon: Coffee, color: 'text-blue-400' }
};

const UnifiedMarcacoes = ({ onVoltar }) => {
  const { jornyxToken, jornyxUser } = useUnifiedAuth();
  const [marcacoes, setMarcacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataSelecionada, setDataSelecionada] = useState(new Date());
  const [resumoMes, setResumoMes] = useState(null);

  useEffect(() => {
    if (jornyxToken) {
      carregarMarcacoesDia();
    }
  }, [jornyxToken, dataSelecionada]);

  const carregarMarcacoesDia = async () => {
    try {
      setLoading(true);
      const dataFormatada = dataSelecionada.toISOString().split('T')[0];
      
      const res = await fetch(`${API_URL}/api/app/marcacoes?data=${dataFormatada}`, {
        headers: { 'Authorization': `Bearer ${jornyxToken}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setMarcacoes(data.marcacoes || []);
        setResumoMes(data.resumo_mes);
      }
    } catch (err) {
      console.error('Erro ao carregar marcações:', err);
      toast.error('Erro ao carregar marcações');
    } finally {
      setLoading(false);
    }
  };

  const mudarDia = (dias) => {
    const novaData = new Date(dataSelecionada);
    novaData.setDate(novaData.getDate() + dias);
    
    // Não permitir data futura
    if (novaData > new Date()) {
      toast.error('Não é possível ver marcações de datas futuras');
      return;
    }
    
    setDataSelecionada(novaData);
  };

  const formatarData = (date) => {
    return date.toLocaleDateString('pt-BR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long',
      year: 'numeric'
    });
  };

  const formatarDataCurta = (date) => {
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const compartilharComprovante = async () => {
    const dataStr = formatarDataCurta(dataSelecionada);
    const marcacoesStr = marcacoes.map(m => 
      `${m.hora} - ${TIPOS_MARCACAO[m.tipo_marcacao]?.label || m.tipo_marcacao}`
    ).join('\n');
    
    const texto = `📋 COMPROVANTE DE PONTO\n` +
      `Funcionário: ${jornyxUser?.nome}\n` +
      `Data: ${dataStr}\n` +
      `\n📍 Marcações:\n${marcacoesStr || 'Nenhuma marcação'}\n` +
      `\n✅ Conforme Portaria MTP 671/2021`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Comprovante de Ponto',
          text: texto
        });
      } catch (e) {
        // Usuário cancelou
      }
    } else {
      // Fallback: copiar para clipboard
      await navigator.clipboard.writeText(texto);
      toast.success('Comprovante copiado para a área de transferência');
    }
  };

  const isHoje = dataSelecionada.toDateString() === new Date().toDateString();

  if (!jornyxToken) {
    return (
      <div className="p-4 text-center text-slate-400">
        <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-400" />
        <p>Faça login para ver suas marcações</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-4 pb-6">
        <div className="flex items-center gap-3 mb-4">
          {onVoltar && (
            <Button variant="ghost" onClick={onVoltar} className="text-white p-2 h-auto">
              <ChevronLeft className="w-6 h-6" />
            </Button>
          )}
          <div>
            <h1 className="text-xl font-bold text-white">Minhas Marcações</h1>
            <p className="text-emerald-100 text-sm">Conforme Portaria MTP 671/2021</p>
          </div>
        </div>
        
        {/* Seletor de data */}
        <div className="flex items-center justify-between bg-white/20 rounded-xl p-3">
          <Button 
            variant="ghost" 
            onClick={() => mudarDia(-1)} 
            className="text-white hover:bg-white/20 p-2 h-auto"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          
          <div className="text-center">
            <p className="text-white font-semibold capitalize">
              {isHoje 
                ? `Hoje, ${dataSelecionada.toLocaleDateString('pt-BR', { weekday: 'long' })}` 
                : dataSelecionada.toLocaleDateString('pt-BR', { weekday: 'long' })
              }
            </p>
            <p className="text-emerald-100 text-sm">
              {formatarDataCurta(dataSelecionada)}
            </p>
          </div>
          
          <Button 
            variant="ghost" 
            onClick={() => mudarDia(1)} 
            className="text-white hover:bg-white/20 p-2 h-auto"
            disabled={isHoje}
          >
            <ChevronRight className={`w-5 h-5 ${isHoje ? 'opacity-30' : ''}`} />
          </Button>
        </div>
      </div>
      
      {/* Conteúdo */}
      <div className="p-4 -mt-2 space-y-4">
        {/* Card de Comprovante */}
        <Card className="bg-white border-0 shadow-xl">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-slate-800 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-emerald-600" />
                Comprovante do Dia
              </CardTitle>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={compartilharComprovante}
                className="border-emerald-200 text-emerald-700"
              >
                <Share2 className="w-4 h-4 mr-1" />
                Compartilhar
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Dados do funcionário */}
            <div className="bg-slate-50 rounded-lg p-3 mb-4">
              <p className="text-sm text-slate-500">Funcionário</p>
              <p className="font-semibold text-slate-800">{jornyxUser?.nome}</p>
              <p className="text-xs text-slate-500">
                {jornyxUser?.matricula && `Matrícula: ${jornyxUser.matricula} • `}
                {jornyxUser?.empresa_nome}
              </p>
            </div>
            
            {/* Lista de marcações */}
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
              </div>
            ) : marcacoes.length === 0 ? (
              <div className="text-center py-8">
                <Clock className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p className="text-slate-500">Nenhuma marcação neste dia</p>
              </div>
            ) : (
              <div className="space-y-2">
                {marcacoes.map((m, i) => {
                  const tipo = TIPOS_MARCACAO[m.tipo_marcacao] || { 
                    label: m.tipo_marcacao, 
                    icon: Clock, 
                    color: 'text-slate-400' 
                  };
                  const Icon = tipo.icon;
                  
                  return (
                    <div 
                      key={m.id || i}
                      className="flex items-center gap-3 bg-slate-50 rounded-lg p-3"
                    >
                      <div className={`p-2 bg-white rounded-full shadow ${tipo.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-slate-800">{m.hora}</p>
                        <p className="text-xs text-slate-500">{tipo.label}</p>
                      </div>
                      <Badge variant="outline" className="text-xs border-slate-200 text-slate-500">
                        {m.origem === 'facial' ? 'Biometria' : m.origem || 'App'}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Assinatura digital */}
            <div className="mt-4 pt-4 border-t border-slate-100 text-center">
              <div className="flex items-center justify-center gap-2 text-emerald-600">
                <CheckCircle className="w-4 h-4" />
                <span className="text-xs font-medium">Comprovante válido - Portaria MTP 671/2021</span>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                Gerado em {new Date().toLocaleString('pt-BR')}
              </p>
            </div>
          </CardContent>
        </Card>
        
        {/* Resumo do Mês */}
        {resumoMes && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-white">
                Resumo de {new Date().toLocaleDateString('pt-BR', { month: 'long' })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-slate-900/50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-emerald-400">{resumoMes.dias_trabalhados || 0}</p>
                  <p className="text-xs text-slate-400">Dias Trabalhados</p>
                </div>
                <div className="bg-slate-900/50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-amber-400">{resumoMes.horas_trabalhadas || '0h'}</p>
                  <p className="text-xs text-slate-400">Horas</p>
                </div>
                <div className="bg-slate-900/50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-blue-400">{resumoMes.banco_horas || '0h'}</p>
                  <p className="text-xs text-slate-400">Banco Horas</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default UnifiedMarcacoes;
